import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import Spinner from '../spinner/spinner';
import { BuyerAuthContext, ENFORCEMENT } from '../../contexts/buyer-auth-context-provider';
import OtpLogin from './otp-login';

/**
 * Decides whether a buyer route draws its page or the OTP screen. No network
 * of its own — the provider has already resolved everything this reads.
 *
 * The gate replaces the page rather than sitting alongside it, which is what
 * stops a frame of somebody else's order painting before the decision lands,
 * and means a mid-session expiry unmounts the page instead of leaving stale
 * state behind a modal.
 */
function AuthGate({ gated, forceGate, children }) {
  const { status, authenticated, enforcement } = useContext(BuyerAuthContext);

  // The same full-page spinner the pages themselves show while loading, so a
  // student sees one continuous spinner rather than spinner, flash, spinner.
  if (status === 'bootstrapping') return <Spinner fullPage />;

  if (!gated) return children;
  if (authenticated) return children;

  // QA escape hatch. Without it observe mode is untestable end to end: the
  // send and verify endpoints work in every mode, so this walks the whole flow
  // while production is still permissive.
  if (forceGate) return <OtpLogin />;

  // Anything that is not explicitly 'enforce' — including an unrecognised
  // value from an older server, or a probe that failed — renders the page. The
  // client must never be the thing standing between a student and a page the
  // server was willing to serve.
  if (enforcement !== ENFORCEMENT.ENFORCE) return children;

  return <OtpLogin />;
}

AuthGate.propTypes = {
  gated: PropTypes.bool,
  forceGate: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

AuthGate.defaultProps = {
  gated: false,
  forceGate: false,
};

export default AuthGate;
